import React, {
  PropsWithChildren,
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from 'react';

/**
 * Mobile Navigation Context Type
 * Manages state for mobile drawer and active route
 */
type MobileNavigationContextType = {
  /** Whether the mobile drawer is currently open */
  drawerOpen: boolean;
  /** Open the mobile drawer */
  openDrawer: () => void;
  /** Close the mobile drawer */
  closeDrawer: () => void;
  /** Toggle the mobile drawer state */
  toggleDrawer: () => void;
  /** Currently active route path */
  activeRoute: string;
  /** Set the active route (also closes drawer) */
  setActiveRoute: (route: string) => void;
};

/**
 * Mobile Navigation Context
 * Provides state management for mobile navigation components
 */
export const MobileNavigationContext = createContext<MobileNavigationContextType>(
  {} as MobileNavigationContextType
);

/**
 * Custom hook to use Mobile Navigation context
 * Must be used within MobileNavigationProvider
 *
 * @returns {MobileNavigationContextType} Mobile navigation context values
 *
 * @example
 * ```tsx
 * const { drawerOpen, toggleDrawer, activeRoute } = useMobileNavigation();
 *
 * <IconButton onClick={toggleDrawer}>
 *   <MenuIcon />
 * </IconButton>
 * ```
 */
export const useMobileNavigation = () => {
  const context = useContext(MobileNavigationContext);
  if (!context) {
    throw new Error(
      'useMobileNavigation must be used within MobileNavigationProvider'
    );
  }
  return context;
};

/**
 * Mobile Navigation Provider
 * Wraps the application to provide mobile navigation state
 *
 * Features:
 * - Drawer state management (open/close/toggle)
 * - Active route tracking
 * - Auto-close drawer on navigation
 * - Persists drawer state to localStorage
 *
 * @example
 * ```tsx
 * <MobileNavigationProvider>
 *   <App />
 * </MobileNavigationProvider>
 * ```
 */
export const MobileNavigationProvider: React.FC<PropsWithChildren> = ({
  children,
}) => {
  // Initialize drawer state from localStorage or default to false
  const [drawerOpen, setDrawerOpen] = useState<boolean>(() => {
    const stored = localStorage.getItem('mobileDrawerOpen');
    if (stored === 'true') return true;
    if (stored === 'false') return false;
    return false; // Default to closed
  });

  // Track active route
  const [activeRoute, setActiveRouteState] = useState<string>('/');

  // Persist drawer state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('mobileDrawerOpen', String(drawerOpen));
  }, [drawerOpen]);

  /**
   * Open the mobile drawer
   */
  const openDrawer = useCallback(() => {
    setDrawerOpen(true);
  }, []);

  /**
   * Close the mobile drawer
   */
  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
  }, []);

  /**
   * Toggle the mobile drawer state
   */
  const toggleDrawer = useCallback(() => {
    setDrawerOpen((prev) => !prev);
  }, []);

  /**
   * Set the active route and auto-close drawer
   * This provides a seamless navigation experience on mobile
   */
  const setActiveRoute = useCallback((route: string) => {
    setActiveRouteState(route);
    // Auto-close drawer when navigating
    setDrawerOpen(false);
  }, []);

  const value: MobileNavigationContextType = {
    drawerOpen,
    openDrawer,
    closeDrawer,
    toggleDrawer,
    activeRoute,
    setActiveRoute,
  };

  return (
    <MobileNavigationContext.Provider value={value}>
      {children}
    </MobileNavigationContext.Provider>
  );
};
